import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import './ProductManager.css';

function ProductManager({ products, onProductsChange }) {
    const [modalOpen, setModalOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        cost: '',
        type: 'digital',
        paymentType: 'one-time',
        fulfillmentTime: 0,
        exclusivity: 'standard',
        additionalInfo: '',
        features: {
            moneyBackGuarantee: false,
            videoCourse: false,
            liveSupport: false,
            communityAccess: false,
            certificate: false,
            lifetimeAccess: false,
            freeUpdates: false,
            oneOnOneCoaching: false,
            bonusContent: false,
            templates: false,
            groupCalls: false,
            prioritySupport: false
        }
    });

    const openAddModal = () => {
        setEditingProduct(null);
        setFormData({
            name: '',
            cost: '',
            type: 'digital',
            paymentType: 'one-time',
            fulfillmentTime: 0,
            exclusivity: 'standard',
            additionalInfo: '',
            features: {
                moneyBackGuarantee: false,
                videoCourse: false,
                liveSupport: false,
                communityAccess: false,
                certificate: false,
                lifetimeAccess: false,
                freeUpdates: false,
                oneOnOneCoaching: false,
                bonusContent: false,
                templates: false,
                groupCalls: false,
                prioritySupport: false
            }
        });
        setModalOpen(true);
    };

    const openEditModal = (product) => {
        setEditingProduct(product.id);
        setFormData({
            name: product.name || '',
            cost: product.cost !== undefined && product.cost !== null ? product.cost.toString() : '',
            type: product.type || 'digital',
            paymentType: product.paymentType || 'one-time',
            fulfillmentTime: product.fulfillmentTime || 0,
            exclusivity: product.exclusivity || 'standard',
            additionalInfo: product.additionalInfo || '',
            features: product.features || {
                moneyBackGuarantee: false,
                videoCourse: false,
                liveSupport: false,
                communityAccess: false,
                certificate: false,
                lifetimeAccess: false,
                freeUpdates: false,
                oneOnOneCoaching: false,
                bonusContent: false,
                templates: false,
                groupCalls: false,
                prioritySupport: false
            }
        });
        setModalOpen(true);
    };

    const handleSave = () => {
        if (!formData.name.trim()) {
            alert('Bitte geben Sie einen Produktnamen ein');
            return;
        }

        // Ensure cost is stored as string to prevent floating point issues
        const cleanedFormData = {
            ...formData,
            cost: formData.cost.toString()
        };

        let newProducts;
        if (editingProduct) {
            // Update existing
            newProducts = products.map(p => 
                p.id === editingProduct ? { ...p, ...cleanedFormData } : p
            );
        } else {
            // Add new
            newProducts = [...products, { id: Date.now(), ...cleanedFormData }];
        }
        
        onProductsChange(newProducts);
        setModalOpen(false);
    };

    const handleRemove = (productId) => {
        if (window.confirm('Möchten Sie dieses Produkt wirklich entfernen?')) {
            onProductsChange(products.filter(p => p.id !== productId));
        }
    };

    const getPriceLabel = (cost) => {
        if (!cost || cost === '' || cost === '0' || cost === 0) {
            return 'Kostenlos';
        }
        // Parse the cost value, handling both string and number inputs
        const numericCost = typeof cost === 'string' ? parseFloat(cost.replace(',', '.')) : cost;
        if (isNaN(numericCost) || numericCost === 0) {
            return 'Kostenlos';
        }
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(numericCost);
    };

    const getTypeLabel = (type) => {
        const labels = {
            'digital': 'Digital',
            'physical': 'Physisch',
            'mix': 'Hybrid'
        };
        return labels[type] || type;
    };

    const getPaymentLabel = (payment) => {
        return payment === 'monthly' ? '💳 Monatlich' : '💰 Einmalig';
    };

    const getActiveFeatures = (features) => {
        if (!features) return [];
        
        const featureLabels = {
            moneyBackGuarantee: { icon: 'fa-shield-alt', label: 'Geld-zurück-Garantie' },
            videoCourse: { icon: 'fa-video', label: 'Videokurs' },
            liveSupport: { icon: 'fa-headset', label: 'Live Support' },
            communityAccess: { icon: 'fa-users', label: 'Community' },
            certificate: { icon: 'fa-certificate', label: 'Zertifikat' },
            lifetimeAccess: { icon: 'fa-infinity', label: 'Lifetime' },
            freeUpdates: { icon: 'fa-sync-alt', label: 'Updates' },
            oneOnOneCoaching: { icon: 'fa-user-tie', label: '1-zu-1 Coaching' },
            bonusContent: { icon: 'fa-gift', label: 'Bonus Content' },
            templates: { icon: 'fa-file-alt', label: 'Vorlagen' },
            groupCalls: { icon: 'fa-phone-volume', label: 'Gruppencalls' },
            prioritySupport: { icon: 'fa-star', label: 'Priority Support' }
        };

        return Object.entries(features)
            .filter(([key, value]) => value === true)
            .map(([key]) => featureLabels[key]);
    };

    // Sortiere Produkte nach Preis (günstigste oben, teuerste unten)
    const sortProductsByPrice = (products) => {
        return [...products].sort((a, b) => {
            const priceA = parseFloat(a.cost) || 0;
            const priceB = parseFloat(b.cost) || 0;
            return priceA - priceB; // Aufsteigend: günstigste zuerst
        });
    };

    return (
        <div className="product-manager">
            {/* Product List */}
            {products.length > 0 && (
                <div className="product-manager-list">
                    {sortProductsByPrice(products.filter(p => p.name?.trim())).map((product) => (
                        <div key={product.id} className="product-manager-card">
                            <div className="product-manager-card-header">
                                <h4>{product.name}</h4>
                                <div className="product-manager-card-actions">
                                    <button
                                        className="product-manager-edit-btn"
                                        onClick={() => openEditModal(product)}
                                        title="Bearbeiten"
                                    >
                                        <i className="fas fa-edit"></i>
                                    </button>
                                    <button
                                        className="product-manager-remove-btn"
                                        onClick={() => handleRemove(product.id)}
                                        title="Löschen"
                                    >
                                        <i className="fas fa-trash"></i>
                                    </button>
                                </div>
                            </div>
                            <div className="product-manager-card-details">
                                <span className="product-manager-badge price">{getPriceLabel(product.cost)}</span>
                                <span className="product-manager-badge type">{getTypeLabel(product.type)}</span>
                                <span className="product-manager-badge payment">{getPaymentLabel(product.paymentType)}</span>
                            </div>
                            {product.additionalInfo && (
                                <p className="product-manager-card-info">{product.additionalInfo}</p>
                            )}
                            {getActiveFeatures(product.features).length > 0 && (
                                <div className="product-manager-card-features">
                                    {getActiveFeatures(product.features).map((feature, idx) => (
                                        <span key={idx} className="product-manager-feature-tag">
                                            <i className={`fas ${feature.icon}`}></i>
                                            {feature.label}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Add Product Button */}
            <button className="product-manager-add-btn" onClick={openAddModal}>
                <i className="fas fa-plus-circle"></i>
                Produkt hinzufügen
            </button>

            {/* Product Modal */}
            {modalOpen && ReactDOM.createPortal(
                <div className="product-manager-modal-overlay">
                    <div className="product-manager-modal">
                        <div className="product-manager-modal-header">
                            <h3>
                                <i className="fas fa-box"></i>
                                {editingProduct ? 'Produkt bearbeiten' : 'Neues Produkt'}
                            </h3>
                            <button className="product-manager-modal-close" onClick={() => setModalOpen(false)}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>

                        <div className="product-manager-modal-body">
                            {/* Produktname */}
                            <div className="product-manager-field">
                                <label>
                                    <i className="fas fa-tag"></i>
                                    Produktname *
                                </label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="z.B. Online Kurs, Coaching, Beratung..."
                                    className="product-manager-input"
                                    autoFocus
                                />
                            </div>

                            {/* Preis */}
                            <div className="product-manager-field">
                                <label>
                                    <i className="fas fa-euro-sign"></i>
                                    Preis (€)
                                </label>
                                <div className="product-manager-price-row">
                                    <input
                                        type="text"
                                        inputMode="decimal"
                                        value={formData.cost}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            // Allow empty, numbers, and decimal point
                                            if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                                setFormData({ ...formData, cost: value });
                                            }
                                        }}
                                        placeholder="0.00"
                                        className="product-manager-input"
                                    />
                                    <button
                                        type="button"
                                        className={`product-manager-freebie-btn ${formData.cost === '0' || formData.cost === '' ? 'active' : ''}`}
                                        onClick={() => setFormData({ ...formData, cost: '0' })}
                                        title="Als kostenloses Produkt markieren"
                                    >
                                        <i className="fas fa-gift"></i>
                                        Freebie
                                    </button>
                                </div>
                                <small className="product-manager-hint">
                                    Geben Sie einen Preis ein oder klicken Sie "Freebie" für kostenlose Produkte
                                </small>
                            </div>

                            {/* Typ & Zahlungsmodell Row */}
                            <div className="product-manager-row">
                                <div className="product-manager-field">
                                    <label>
                                        <i className="fas fa-boxes"></i>
                                        Typ
                                    </label>
                                    <div className="product-manager-toggle-group">
                                        {[
                                            { value: 'digital', label: 'Digital', icon: 'fas fa-laptop' },
                                            { value: 'physical', label: 'Physisch', icon: 'fas fa-box' },
                                            { value: 'mix', label: 'Hybrid', icon: 'fas fa-layer-group' }
                                        ].map((type) => (
                                            <label
                                                key={type.value}
                                                className={`product-manager-toggle ${formData.type === type.value ? 'selected' : ''}`}
                                            >
                                                <input
                                                    type="radio"
                                                    name="type"
                                                    value={type.value}
                                                    checked={formData.type === type.value}
                                                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                                                />
                                                <i className={type.icon}></i>
                                                <span>{type.label}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                <div className="product-manager-field">
                                    <label>
                                        <i className="fas fa-credit-card"></i>
                                        Zahlung
                                    </label>
                                    <div className="product-manager-toggle-group">
                                        {[
                                            { value: 'one-time', label: 'Einmalig', icon: 'fas fa-shopping-cart' },
                                            { value: 'monthly', label: 'Monatlich', icon: 'fas fa-sync-alt' }
                                        ].map((payment) => (
                                            <label
                                                key={payment.value}
                                                className={`product-manager-toggle ${formData.paymentType === payment.value ? 'selected' : ''}`}
                                            >
                                                <input
                                                    type="radio"
                                                    name="paymentType"
                                                    value={payment.value}
                                                    checked={formData.paymentType === payment.value}
                                                    onChange={(e) => setFormData({ ...formData, paymentType: e.target.value })}
                                                />
                                                <i className={payment.icon}></i>
                                                <span>{payment.label}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Exklusivität */}
                            <div className="product-manager-field">
                                <label>
                                    <i className="fas fa-star"></i>
                                    Exklusivität
                                </label>
                                <div className="product-manager-toggle-group">
                                    {[
                                        { value: 'standard', label: 'Standard', icon: 'fas fa-box' },
                                        { value: 'unique', label: 'Einzigartig', icon: 'fas fa-gem' },
                                        { value: 'revolutionary', label: 'Revolutionär', icon: 'fas fa-rocket' }
                                    ].map((exclusivity) => (
                                        <label
                                            key={exclusivity.value}
                                            className={`product-manager-toggle ${formData.exclusivity === exclusivity.value ? 'selected' : ''}`}
                                        >
                                            <input
                                                type="radio"
                                                name="exclusivity"
                                                value={exclusivity.value}
                                                checked={formData.exclusivity === exclusivity.value}
                                                onChange={(e) => setFormData({ ...formData, exclusivity: e.target.value })}
                                            />
                                            <i className={exclusivity.icon}></i>
                                            <span>{exclusivity.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Fulfillment Time */}
                            <div className="product-manager-field">
                                <label>
                                    <i className="fas fa-clock"></i>
                                    Fulfillment-Dauer
                                </label>
                                <div className="product-manager-slider-container">
                                    <input
                                        type="range"
                                        min="0"
                                        max="50"
                                        value={formData.fulfillmentTime}
                                        onChange={(e) => setFormData({ ...formData, fulfillmentTime: parseInt(e.target.value) })}
                                        className="product-manager-slider"
                                    />
                                    <div className="product-manager-slider-value">
                                        <span>{formData.fulfillmentTime === 0 ? '⚡ Sofort' : `${formData.fulfillmentTime}h`}</span>
                                        <small>
                                            {formData.fulfillmentTime === 0 ? 'Automatisch' :
                                             formData.fulfillmentTime <= 10 ? 'Schnell' :
                                             formData.fulfillmentTime <= 30 ? 'Standard' : 'Intensiv'}
                                        </small>
                                    </div>
                                </div>
                            </div>

                            {/* Additional Info */}
                            <div className="product-manager-field">
                                <label>
                                    <i className="fas fa-info-circle"></i>
                                    Zusätzliche Informationen (Optional)
                                </label>
                                <textarea
                                    value={formData.additionalInfo}
                                    onChange={(e) => setFormData({ ...formData, additionalInfo: e.target.value })}
                                    placeholder="Weitere Details zu diesem Produkt..."
                                    className="product-manager-textarea"
                                    rows="2"
                                />
                            </div>

                            {/* Product Features */}
                            <div className="product-manager-field">
                                <label>
                                    <i className="fas fa-sparkles"></i>
                                    Premium Features
                                </label>
                                <div className="product-manager-features-grid">
                                    <button
                                        type="button"
                                        className={`product-manager-feature-btn ${formData.features.moneyBackGuarantee ? 'active' : ''}`}
                                        onClick={() => setFormData({ 
                                            ...formData, 
                                            features: { ...formData.features, moneyBackGuarantee: !formData.features.moneyBackGuarantee }
                                        })}
                                    >
                                        <i className="fas fa-shield-alt"></i>
                                        <span>Geld-zurück-Garantie</span>
                                    </button>
                                    
                                    <button
                                        type="button"
                                        className={`product-manager-feature-btn ${formData.features.videoCourse ? 'active' : ''}`}
                                        onClick={() => setFormData({ 
                                            ...formData, 
                                            features: { ...formData.features, videoCourse: !formData.features.videoCourse }
                                        })}
                                    >
                                        <i className="fas fa-video"></i>
                                        <span>Videokurs</span>
                                    </button>

                                    <button
                                        type="button"
                                        className={`product-manager-feature-btn ${formData.features.liveSupport ? 'active' : ''}`}
                                        onClick={() => setFormData({ 
                                            ...formData, 
                                            features: { ...formData.features, liveSupport: !formData.features.liveSupport }
                                        })}
                                    >
                                        <i className="fas fa-headset"></i>
                                        <span>Live Support</span>
                                    </button>

                                    <button
                                        type="button"
                                        className={`product-manager-feature-btn ${formData.features.communityAccess ? 'active' : ''}`}
                                        onClick={() => setFormData({ 
                                            ...formData, 
                                            features: { ...formData.features, communityAccess: !formData.features.communityAccess }
                                        })}
                                    >
                                        <i className="fas fa-users"></i>
                                        <span>Community-Zugang</span>
                                    </button>

                                    <button
                                        type="button"
                                        className={`product-manager-feature-btn ${formData.features.certificate ? 'active' : ''}`}
                                        onClick={() => setFormData({ 
                                            ...formData, 
                                            features: { ...formData.features, certificate: !formData.features.certificate }
                                        })}
                                    >
                                        <i className="fas fa-certificate"></i>
                                        <span>Zertifikat</span>
                                    </button>

                                    <button
                                        type="button"
                                        className={`product-manager-feature-btn ${formData.features.lifetimeAccess ? 'active' : ''}`}
                                        onClick={() => setFormData({ 
                                            ...formData, 
                                            features: { ...formData.features, lifetimeAccess: !formData.features.lifetimeAccess }
                                        })}
                                    >
                                        <i className="fas fa-infinity"></i>
                                        <span>Lebenslanger Zugang</span>
                                    </button>

                                    <button
                                        type="button"
                                        className={`product-manager-feature-btn ${formData.features.freeUpdates ? 'active' : ''}`}
                                        onClick={() => setFormData({ 
                                            ...formData, 
                                            features: { ...formData.features, freeUpdates: !formData.features.freeUpdates }
                                        })}
                                    >
                                        <i className="fas fa-sync-alt"></i>
                                        <span>Kostenlose Updates</span>
                                    </button>

                                    <button
                                        type="button"
                                        className={`product-manager-feature-btn ${formData.features.oneOnOneCoaching ? 'active' : ''}`}
                                        onClick={() => setFormData({ 
                                            ...formData, 
                                            features: { ...formData.features, oneOnOneCoaching: !formData.features.oneOnOneCoaching }
                                        })}
                                    >
                                        <i className="fas fa-user-tie"></i>
                                        <span>1-zu-1 Coaching</span>
                                    </button>

                                    <button
                                        type="button"
                                        className={`product-manager-feature-btn ${formData.features.bonusContent ? 'active' : ''}`}
                                        onClick={() => setFormData({ 
                                            ...formData, 
                                            features: { ...formData.features, bonusContent: !formData.features.bonusContent }
                                        })}
                                    >
                                        <i className="fas fa-gift"></i>
                                        <span>Bonus Content</span>
                                    </button>

                                    <button
                                        type="button"
                                        className={`product-manager-feature-btn ${formData.features.templates ? 'active' : ''}`}
                                        onClick={() => setFormData({ 
                                            ...formData, 
                                            features: { ...formData.features, templates: !formData.features.templates }
                                        })}
                                    >
                                        <i className="fas fa-file-alt"></i>
                                        <span>Vorlagen</span>
                                    </button>

                                    <button
                                        type="button"
                                        className={`product-manager-feature-btn ${formData.features.groupCalls ? 'active' : ''}`}
                                        onClick={() => setFormData({ 
                                            ...formData, 
                                            features: { ...formData.features, groupCalls: !formData.features.groupCalls }
                                        })}
                                    >
                                        <i className="fas fa-phone-volume"></i>
                                        <span>Gruppencalls</span>
                                    </button>

                                    <button
                                        type="button"
                                        className={`product-manager-feature-btn ${formData.features.prioritySupport ? 'active' : ''}`}
                                        onClick={() => setFormData({ 
                                            ...formData, 
                                            features: { ...formData.features, prioritySupport: !formData.features.prioritySupport }
                                        })}
                                    >
                                        <i className="fas fa-star"></i>
                                        <span>Priority Support</span>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="product-manager-modal-footer">
                            <button className="product-manager-cancel-btn" onClick={() => setModalOpen(false)}>
                                Abbrechen
                            </button>
                            <button className="product-manager-save-btn" onClick={handleSave}>
                                <i className="fas fa-check"></i>
                                {editingProduct ? 'Aktualisieren' : 'Hinzufügen'}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}

export default ProductManager;

